// src/components/Settings.js
import { useContext, useEffect, useState } from 'react';
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  TextField,
  Stack,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import SettingsIcon from '@mui/icons-material/Settings';
import LogOutButton from './LogOutButton';
import { UserContext } from '../contexts/UserContext';
import { auth } from '../firebase';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  updateProfile,
} from 'firebase/auth';

const GOLD = '#d4af37';
const WARM = '#f9f6e3';
const PANEL_BG = 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)';

const GoldSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: 'rgba(212,175,55,0.6)',
  },
  '& .MuiSwitch-track': { backgroundColor: 'rgba(212,175,55,0.25)' },
}));

function Settings() {
  const { userData, persistUserData } = useContext(UserContext);

  const [open, setOpen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(
    userData?.settings?.soundEnabled ?? true
  );

  // Link-account dialog state
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkUsername, setLinkUsername] = useState('');
  const [linkError, setLinkError] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);

  const isAnonymous = auth.currentUser?.isAnonymous;

  // Keep local state in sync when userData.settings changes
  useEffect(() => {
    setSoundEnabled(userData?.settings?.soundEnabled ?? true);
  }, [userData?.settings?.soundEnabled]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleToggleSound = async (event) => {
    const checked = !!event.target.checked;
    setSoundEnabled(checked);

    if (!userData || !persistUserData) return;

    try {
      const nextSettings = {
        ...(userData.settings || {}),
        soundEnabled: checked,
      };
      await persistUserData({ settings: nextSettings });
    } catch (err) {
      console.error('Failed to update sound setting:', err);
    }
  };

  const username = isAnonymous
    ? userData?.username || 'Guest Commander'
    : userData?.username || 'Unknown';

  const email = isAnonymous ? 'Not linked' : userData?.email || 'Unknown';

  const handleOpenLinkDialog = () => {
    setLinkEmail(userData?.email || '');
    setLinkUsername(userData?.username || '');
    setLinkPassword('');
    setLinkError('');
    setLinkOpen(true);
  };

  const handleCloseLinkDialog = () => {
    if (linkLoading) return;
    setLinkOpen(false);
  };

  const handleLinkWithEmail = async () => {
    setLinkError('');
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setLinkError('No active user to link.');
      return;
    }

    if (!linkEmail || !linkPassword || !linkUsername) {
      setLinkError('Please fill email, username, and password.');
      return;
    }

    try {
      setLinkLoading(true);

      const credential = EmailAuthProvider.credential(linkEmail, linkPassword);
      const result = await linkWithCredential(currentUser, credential);

      await updateProfile(result.user, { displayName: linkUsername });

      if (persistUserData) {
        await persistUserData({
          email: linkEmail,
          username: linkUsername,
        });
      }

      setLinkOpen(false);
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/email-already-in-use') {
        setLinkError('That email is already used by another account.');
      } else {
        setLinkError(err?.message || 'Failed to link account.');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  const handleLinkWithGoogle = async () => {
    setLinkError('');
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setLinkError('No active user to link.');
      return;
    }

    try {
      setLinkLoading(true);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });

      const result = await linkWithPopup(currentUser, provider);

      const newEmail = result.user.email;
      const newUsername = result.user.displayName;

      if (persistUserData) {
        const updates = {};
        if (newEmail) updates.email = newEmail;
        if (newUsername && !userData?.username) updates.username = newUsername;

        if (Object.keys(updates).length > 0) {
          await persistUserData(updates);
        }
      }

      setLinkOpen(false);
    } catch (err) {
      console.error(err);
      if (
        err?.code === 'auth/credential-already-in-use' ||
        err?.code === 'auth/account-exists-with-different-credential'
      ) {
        setLinkError(
          'That Google account is already linked to another profile.'
        );
      } else if (err?.code === 'auth/popup-closed-by-user') {
        // user cancelled
      } else {
        setLinkError(err?.message || 'Failed to link Google account.');
      }
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <>
      {/* Gear icon */}
      <Tooltip title="Settings">
        <IconButton
          onClick={handleOpen}
          sx={{
            position: 'absolute',
            top: 100,
            right: 50,
            color: GOLD,
            background: alpha('#000', 0.5),
            border: `1px solid ${alpha(GOLD, 0.4)}`,
            boxShadow: `0 0 12px ${alpha(GOLD, 0.4)}`,
            '&:hover': {
              background: alpha(GOLD, 0.15),
            },
          }}
        >
          <SettingsIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Tooltip>

      {/* Settings dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: PANEL_BG,
            border: '1px solid rgba(212,175,55,0.7)',
            boxShadow: '0 0 28px rgba(212,175,55,0.45)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: `'Cinzel', serif`,
            letterSpacing: '0.8px',
            fontWeight: 700,
            color: WARM,
            textShadow: '0 0 12px rgba(212,175,55,0.6)',
            pb: 1,
          }}
        >
          Settings
        </DialogTitle>

        <DialogContent sx={{ color: WARM, pt: 1 }}>
          {/* Account Info */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: `'Cinzel', serif`,
                letterSpacing: '0.6px',
                color: GOLD,
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Account
            </Typography>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid rgba(212,175,55,0.35)',
                backgroundColor: 'rgba(0,0,0,0.7)',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(249,246,227,0.8)',
                  mb: 0.5,
                }}
              >
                <strong>Username:</strong> {username}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(249,246,227,0.8)',
                }}
              >
                <strong>Email:</strong> {email}
              </Typography>

              {isAnonymous && (
                <Box sx={{ mt: 1.5, textAlign: 'center' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      color: 'rgba(249,246,227,0.7)',
                      mb: 1,
                    }}
                  >
                    You&apos;re playing as a guest. Link your account to protect
                    your cards and progress across devices.
                  </Typography>

                  <Button
                    onClick={handleOpenLinkDialog}
                    sx={{
                      mt: 0.5,
                      fontFamily: `'Cinzel', serif`,
                      textTransform: 'uppercase',
                      letterSpacing: '0.8px',
                      color: WARM,
                      borderRadius: 2,
                      border: '1px solid rgba(212,175,55,0.8)',
                      px: 2.5,
                      py: 0.4,
                      background:
                        'linear-gradient(135deg, rgba(212,175,55,0.25), rgba(0,0,0,0.9))',
                      boxShadow: '0 0 14px rgba(212,175,55,0.4)',
                      '&:hover': {
                        background:
                          'linear-gradient(135deg, rgba(212,175,55,0.35), rgba(0,0,0,0.95))',
                        boxShadow: '0 0 20px rgba(212,175,55,0.7)',
                      },
                    }}
                  >
                    Link Account
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          <Divider
            sx={{
              borderColor: GOLD,
              opacity: 0.5,
              my: 1.5,
            }}
          />

          {/* Audio settings */}
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontFamily: `'Cinzel', serif`,
                letterSpacing: '0.6px',
                color: GOLD,
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Audio
            </Typography>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid rgba(212,175,55,0.35)',
                backgroundColor: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: WARM, mb: 0.2 }}>
                  Sound Effects
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(249,246,227,0.75)',
                  }}
                >
                  Toggle in-game sounds and effects.
                </Typography>
              </Box>
              <FormControlLabel
                control={
                  <GoldSwitch checked={!!soundEnabled} onChange={handleToggleSound} />
                }
                label={soundEnabled ? 'On' : 'Off'}
                sx={{
                  ml: 1,
                  color: WARM,
                  '& .MuiFormControlLabel-label': {
                    fontFamily: `'Cinzel', serif`,
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
            <LogOutButton />
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              ml: 'auto',
              fontFamily: `'Cinzel', serif`,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              color: WARM,
              borderRadius: 2,
              border: '1px solid rgba(212,175,55,0.7)',
              px: 2.5,
              py: 0.5,
              background:
                'linear-gradient(135deg, rgba(212,175,55,0.25), rgba(0,0,0,0.9))',
              boxShadow: '0 0 12px rgba(212,175,55,0.35)',
              '&:hover': {
                background:
                  'linear-gradient(135deg, rgba(212,175,55,0.35), rgba(0,0,0,0.95))',
                boxShadow: '0 0 18px rgba(212,175,55,0.6)',
              },
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Link Account dialog (for guests) */}
      <Dialog
        open={linkOpen}
        onClose={handleCloseLinkDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            background: PANEL_BG,
            border: '1px solid rgba(212,175,55,0.7)',
            boxShadow: '0 0 28px rgba(212,175,55,0.6)',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: `'Cinzel', serif`,
            letterSpacing: '0.8px',
            fontWeight: 700,
            color: WARM,
            textShadow: '0 0 12px rgba(212,175,55,0.6)',
            pb: 1,
          }}
        >
          Link Your Account
        </DialogTitle>

        <DialogContent sx={{ color: WARM, pt: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 2,
              color: 'rgba(249,246,227,0.8)',
            }}
          >
            Create a full account so you don&apos;t lose your cards and progress. You
            can link with email or with Google.
          </Typography>

          <Stack spacing={1.5}>
            <TextField
              label="Username"
              fullWidth
              value={linkUsername}
              onChange={(e) => setLinkUsername(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(249,246,227,0.75)',
                  fontFamily: `'Cinzel', serif`,
                  '&.Mui-focused': {
                    color: GOLD,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  color: WARM,
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: 'rgba(212,175,55,0.4)',
                  },
                  '&:hover fieldset': {
                    borderColor: GOLD,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: GOLD,
                    boxShadow:
                      '0 0 0 1px rgba(212,175,55,0.7), 0 0 18px rgba(212,175,55,0.45)',
                  },
                },
              }}
            />

            <TextField
              label="Email"
              type="email"
              fullWidth
              value={linkEmail}
              onChange={(e) => setLinkEmail(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(249,246,227,0.75)',
                  fontFamily: `'Cinzel', serif`,
                  '&.Mui-focused': {
                    color: GOLD,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  color: WARM,
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: 'rgba(212,175,55,0.4)',
                  },
                  '&:hover fieldset': {
                    borderColor: GOLD,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: GOLD,
                    boxShadow:
                      '0 0 0 1px rgba(212,175,55,0.7), 0 0 18px rgba(212,175,55,0.45)',
                  },
                },
              }}
            />

            <TextField
              label="Password"
              type="password"
              fullWidth
              value={linkPassword}
              onChange={(e) => setLinkPassword(e.target.value)}
              variant="outlined"
              sx={{
                '& .MuiInputLabel-root': {
                  color: 'rgba(249,246,227,0.75)',
                  fontFamily: `'Cinzel', serif`,
                  '&.Mui-focused': {
                    color: GOLD,
                  },
                },
                '& .MuiOutlinedInput-root': {
                  color: WARM,
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  borderRadius: 1.5,
                  '& fieldset': {
                    borderColor: 'rgba(212,175,55,0.4)',
                  },
                  '&:hover fieldset': {
                    borderColor: GOLD,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: GOLD,
                    boxShadow:
                      '0 0 0 1px rgba(212,175,55,0.7), 0 0 18px rgba(212,175,55,0.45)',
                  },
                },
              }}
            />

            {linkError && (
              <Typography
                sx={{
                  color: '#ffb4a9',
                  fontSize: 13,
                  mt: 0.5,
                }}
              >
                {linkError}
              </Typography>
            )}

            <Typography
              variant="caption"
              sx={{
                color: 'rgba(249,246,227,0.7)',
                mt: 1,
              }}
            >
              Email + password will let you log in on any device. You can also link
              with Google below.
            </Typography>

            <Button
              onClick={handleLinkWithEmail}
              disabled={linkLoading}
              sx={{
                mt: 1,
                fontFamily: `'Cinzel', serif`,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                color: '#0b0b0b',
                borderRadius: 2,
                backgroundImage: `linear-gradient(180deg, ${GOLD}, #b68c1e)`,
                boxShadow: '0 8px 24px rgba(212,175,55,0.45)',
                '&:hover': {
                  backgroundImage: `linear-gradient(180deg, ${GOLD}, #a37c1a)`,
                  boxShadow: '0 10px 30px rgba(212,175,55,0.6)',
                },
              }}
            >
              {linkLoading ? 'Linking...' : 'Link with Email'}
            </Button>

            <Button
              onClick={handleLinkWithGoogle}
              disabled={linkLoading}
              variant="outlined"
              sx={{
                mt: 0.5,
                fontFamily: `'Cinzel', serif`,
                textTransform: 'none',
                letterSpacing: '0.6px',
                color: WARM,
                borderRadius: 2,
                border: '1px solid rgba(212,175,55,0.8)',
                background:
                  'linear-gradient(135deg, rgba(0,0,0,0.9), rgba(212,175,55,0.15))',
                '&:hover': {
                  borderColor: GOLD,
                  boxShadow: '0 0 20px rgba(212,175,55,0.6)',
                },
              }}
            >
              {linkLoading ? 'Linking...' : 'Link with Google'}
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            pb: 2,
            pt: 1,
          }}
        >
          <Button
            onClick={handleCloseLinkDialog}
            sx={{
              ml: 'auto',
              fontFamily: `'Cinzel', serif`,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
              color: WARM,
              borderRadius: 2,
              border: '1px solid rgba(212,175,55,0.7)',
              px: 2.5,
              py: 0.5,
              background:
                'linear-gradient(135deg, rgba(212,175,55,0.25), rgba(0,0,0,0.9))',
              boxShadow: '0 0 12px rgba(212,175,55,0.35)',
              '&:hover': {
                background:
                  'linear-gradient(135deg, rgba(212,175,55,0.35), rgba(0,0,0,0.95))',
                boxShadow: '0 0 18px rgba(212,175,55,0.6)',
              },
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Settings;
